import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FiBarChart2, FiDownload, FiFileText, FiFile, FiChevronDown, FiCheck,
  FiSearch, FiX, FiActivity, FiFilePlus, FiRefreshCw, FiEye, FiCheckCircle, FiFlag,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { activityReportAPI } from '../../services/api';
import { formatDateTimeFull, formatDateMedium } from '../../utils/dateFormat';
import { auditDescription } from '../../utils/auditI18n';
import PageHeader from '../../components/PageHeader/PageHeader';
import DataGrid from '../../components/DataGrid';
import './ActivityReport.scss';

/**
 * Faaliyet Raporu — every user activity the system records (contract
 * lifecycle + workflow step actions), compiled automatically and reportable
 * by date range, company, user, department, contract type, flow step, step
 * type and action. KPI cards and the per-user summary follow the same
 * filters as the detail list.
 */

const STEP_TYPE_CODES = ['review', 'control', 'approval', 'information'];

const ACTION_CODES = [
  'contract_created', 'contract_updated', 'responsible_updated',
  'version_created', 'version_activated', 'returned_to_revision',
  'document_uploaded', 'document_created', 'document_version_created',
  'workflow_started', 'step_forwarded', 'opinion_requested', 'opinion_given',
  'signature_requested', 'signature_completed', 'signing_cancelled',
  'step_reviewed', 'control_passed', 'control_failed',
  'step_approved', 'step_rejected', 'changes_requested', 'step_informed',
  'contract_completed',
];

// Compact multi-select: trigger shows a count, dropdown offers search +
// checkboxes. Values are compared as strings.
function MultiSelect({ options, values, onChange, placeholder }) {
  const { t } = useTranslation();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label || '').toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (v) => {
    const sv = String(v);
    onChange(values.includes(sv) ? values.filter((x) => x !== sv) : [...values, sv]);
  };

  const triggerLabel = values.length === 0
    ? (placeholder || t('common.all', { defaultValue: 'All' }))
    : values.length === 1
      ? (options.find((o) => String(o.value) === values[0])?.label || `1 ${t('activityReport.filters.selectedSuffix')}`)
      : t('activityReport.filters.selectedCount', { count: values.length });

  return (
    <div className={`ar-msel${open ? ' is-open' : ''}`} ref={wrapRef}>
      <button type="button" className={`ar-msel__trigger${values.length ? ' has-value' : ''}`} onClick={() => setOpen((o) => !o)}>
        <span className="ar-msel__label">{triggerLabel}</span>
        {values.length > 0 && (
          <span
            className="ar-msel__clear"
            role="button"
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
          >
            <FiX />
          </span>
        )}
        <FiChevronDown className="ar-msel__chevron" />
      </button>
      {open && (
        <div className="ar-msel__pop">
          <div className="ar-msel__search">
            <FiSearch />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('common.search', { defaultValue: 'Search…' })}
              autoFocus
            />
          </div>
          <div className="ar-msel__list">
            {filtered.length === 0 ? (
              <div className="ar-msel__empty">{t('common.noResults', { defaultValue: 'No results' })}</div>
            ) : filtered.map((o) => {
              const checked = values.includes(String(o.value));
              return (
                <div
                  key={o.value}
                  className={`ar-msel__option${checked ? ' is-checked' : ''}`}
                  onClick={() => toggle(o.value)}
                >
                  <span className="ar-msel__box">{checked && <FiCheck />}</span>
                  <span>{o.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityReport() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  // Tenant branding accent — same source as the Dashboard KPIs.
  const accent = useSelector(
    (s) => s.settings?.branding?.accentColor || s.settings?.config?.branding?.accentColor
  ) || '#6366f1';

  const [meta, setMeta] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);

  // ── Filters ───────────────────────────────────────────────────────────────
  const [preset, setPreset] = useState('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [companyIds, setCompanyIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [departmentIds, setDepartmentIds] = useState([]);
  const [contractTypeIds, setContractTypeIds] = useState([]);
  const [stepNames, setStepNames] = useState([]);
  const [stepTypes, setStepTypes] = useState([]);
  const [actions, setActions] = useState([]);

  const range = useMemo(() => {
    const now = new Date();
    if (preset === 'week') return { from: new Date(now.getTime() - 7 * 86400000).toISOString(), to: now.toISOString() };
    if (preset === 'month') { const d = new Date(now); d.setMonth(d.getMonth() - 1); return { from: d.toISOString(), to: now.toISOString() }; }
    if (preset === 'year') return { from: new Date(now.getFullYear(), 0, 1).toISOString(), to: now.toISOString() };
    // custom: date-only strings; the backend widens `to` to the end of the day
    return { from: customFrom || '', to: customTo || '' };
  }, [preset, customFrom, customTo]);

  const params = useMemo(() => ({
    lang,
    from: range.from || undefined,
    to: range.to || undefined,
    companyIds: companyIds.join(',') || undefined,
    userIds: userIds.join(',') || undefined,
    departmentIds: departmentIds.join(',') || undefined,
    contractTypeIds: contractTypeIds.join(',') || undefined,
    stepNames: stepNames.join(',') || undefined,
    stepTypes: stepTypes.join(',') || undefined,
    actions: actions.join(',') || undefined,
  }), [lang, range, companyIds, userIds, departmentIds, contractTypeIds, stepNames, stepTypes, actions]);

  // ── Data loading ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    activityReportAPI.meta({ lang })
      .then(({ data }) => { if (!cancelled) setMeta(data); })
      .catch(() => { if (!cancelled) toast.error(t('activityReport.messages.loadFailed')); });
    return () => { cancelled = true; };
  }, [lang, t]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    activityReportAPI.report(params)
      .then(({ data }) => { if (!cancelled) setReport(data); })
      .catch(() => { if (!cancelled) toast.error(t('activityReport.messages.loadFailed')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [params, t]);

  // Flow-step options follow the Contract Type selection: only the steps of
  // workflows serving the chosen types remain (steps with contractTypeIds:
  // null serve every type).
  const stepOptions = useMemo(() => {
    if (!meta) return [];
    let steps = meta.steps || [];
    if (contractTypeIds.length) {
      const sel = new Set(contractTypeIds);
      const narrowed = steps.filter((s) => s.contractTypeIds === null || s.contractTypeIds.some((id) => sel.has(id)));
      if (narrowed.length) steps = narrowed;
    }
    const seen = new Set();
    return steps.filter((s) => (seen.has(s.name) ? false : seen.add(s.name)))
      .map((s) => ({ value: s.name, label: s.name }));
  }, [meta, contractTypeIds]);

  const stepTypeOptions = useMemo(
    () => STEP_TYPE_CODES.map((c) => ({ value: c, label: t(`activityReport.stepTypes.${c}`) })), [t]
  );
  const actionOptions = useMemo(
    () => ACTION_CODES.map((c) => ({ value: c, label: t(`activityReport.actions.${c}`) })), [t]
  );

  const clearFilters = () => {
    setPreset('month'); setCustomFrom(''); setCustomTo('');
    setCompanyIds([]); setUserIds([]); setDepartmentIds([]);
    setContractTypeIds([]); setStepNames([]); setStepTypes([]); setActions([]);
  };

  // ── Export helpers ───────────────────────────────────────────────────────
  const rangeLabel = useMemo(() => {
    const presetLabel = t(`activityReport.presets.${preset}`);
    const from = range.from ? formatDateMedium(range.from) : '—';
    const to = range.to ? formatDateMedium(range.to) : '—';
    return `${presetLabel} (${from} – ${to})`;
  }, [preset, range, t]);

  const filtersLabel = useMemo(() => {
    if (!meta) return '';
    const parts = [];
    const named = (ids, list) => ids
      .map((id) => list.find((x) => String(x.id) === id)?.name || id).join(', ');
    if (companyIds.length) parts.push(`${t('activityReport.filters.company')}: ${named(companyIds, meta.companies)}`);
    if (userIds.length) parts.push(`${t('activityReport.filters.user')}: ${named(userIds, meta.users)}`);
    if (departmentIds.length) parts.push(`${t('activityReport.filters.department')}: ${named(departmentIds, meta.departments)}`);
    if (contractTypeIds.length) parts.push(`${t('activityReport.filters.contractType')}: ${named(contractTypeIds, meta.contractTypes)}`);
    if (stepNames.length) parts.push(`${t('activityReport.filters.step')}: ${stepNames.join(', ')}`);
    if (stepTypes.length) parts.push(`${t('activityReport.filters.stepType')}: ${stepTypes.map((c) => t(`activityReport.stepTypes.${c}`)).join(', ')}`);
    if (actions.length) parts.push(`${t('activityReport.filters.action')}: ${actions.map((c) => t(`activityReport.actions.${c}`)).join(', ')}`);
    return parts.join(' • ');
  }, [meta, companyIds, userIds, departmentIds, contractTypeIds, stepNames, stepTypes, actions, t]);

  const describeRow = useCallback((r) => (
    r.id?.startsWith('ae_') ? auditDescription(r.description, t, i18n.language) : (r.description || '')
  ), [t, i18n.language]);

  // Duration as compact units — "2g 4s 13dk" / "2d 4h 13m". One-shot
  // activities (create, upload, …) have no span and render as "—".
  const formatDuration = useCallback((ms) => {
    if (ms == null) return '';
    const mins = Math.round(ms / 60000);
    const d = Math.floor(mins / 1440);
    const h = Math.floor((mins % 1440) / 60);
    const m = mins % 60;
    const parts = [];
    if (d) parts.push(`${d}${t('activityReport.durationUnits.d')}`);
    if (h) parts.push(`${h}${t('activityReport.durationUnits.h')}`);
    if (m || !parts.length) parts.push(`${m}${t('activityReport.durationUnits.m')}`);
    return parts.join(' ');
  }, [t]);

  const handleExportExcel = () => {
    if (!report) return;
    const kpiRows = [
      [t('activityReport.kpis.total'), report.kpis.total],
      [t('activityReport.kpis.contractsCreated'), report.kpis.contractsCreated],
      [t('activityReport.kpis.revisions'), report.kpis.revisions],
      [t('activityReport.kpis.reviewControl'), report.kpis.reviewControl],
      [t('activityReport.kpis.approvalActions'), report.kpis.approvalActions],
      [t('activityReport.kpis.contractsCompleted'), report.kpis.contractsCompleted],
    ];
    const summaryHeader = ['user', 'total', 'created', 'revision', 'review', 'control', 'approval', 'completed', 'other']
      .map((k) => t(`activityReport.summary.${k}`));
    const summaryRows = report.userSummary.map((s) => [
      s.userName || '—', s.total, s.created, s.revision, s.review, s.control, s.approval, s.completed, s.other,
    ]);

    const overview = [
      [t('activityReport.title')],
      [t('activityReport.export.generated'), formatDateTimeFull(new Date())],
      [t('activityReport.export.range'), rangeLabel],
      [t('activityReport.export.filters'), filtersLabel || t('activityReport.export.noFilters')],
      [],
      [t('activityReport.export.kpis')],
      ...kpiRows,
      [],
      [t('activityReport.summary.title')],
      summaryHeader,
      ...summaryRows,
    ];

    const detailHeader = ['actionStart', 'actionEnd', 'duration', 'user', 'company', 'department', 'contractNumber', 'contractName', 'contractType', 'step', 'stepType', 'action', 'description']
      .map((k) => t(`activityReport.table.${k}`));
    const detailRows = (report.activities || []).map((r) => [
      r.startedAt ? format(new Date(r.startedAt), 'yyyy-MM-dd HH:mm:ss') : '',
      r.timestamp ? format(new Date(r.timestamp), 'yyyy-MM-dd HH:mm:ss') : '',
      formatDuration(r.durationMs),
      r.userName || '', r.companyName || '', r.departmentName || '',
      r.contractNumber || '', r.contractName || '', r.contractTypeName || '',
      r.stepName || '', r.stepType ? t(`activityReport.stepTypes.${r.stepType}`) : '',
      t(`activityReport.actions.${r.action}`, { defaultValue: r.action }),
      describeRow(r),
    ]);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(overview), t('activityReport.export.summarySheet').slice(0, 31));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([detailHeader, ...detailRows]), t('activityReport.export.detailsSheet').slice(0, 31));
    XLSX.writeFile(wb, `${t('activityReport.export.fileName')}_${format(new Date(), 'yyyy-MM-dd_HHmm')}.xlsx`);
    toast.success(t('activityReport.messages.exportSuccess'));
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const { data } = await activityReportAPI.exportPdf({
        ...params,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        rangeText: rangeLabel,
        filtersText: filtersLabel,
      });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('activityReport.export.fileName')}_${format(new Date(), 'yyyy-MM-dd_HHmm')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('activityReport.messages.exportSuccess'));
    } catch {
      toast.error(t('activityReport.messages.pdfFailed'));
    } finally {
      setExportingPdf(false);
    }
  };

  // ── KPI cards ────────────────────────────────────────────────────────────
  const kpis = report ? [
    { key: 'total', value: report.kpis.total, icon: FiActivity, color: accent },
    { key: 'contractsCreated', value: report.kpis.contractsCreated, icon: FiFilePlus, color: '#0ea5e9' },
    { key: 'revisions', value: report.kpis.revisions, icon: FiRefreshCw, color: '#f59e0b' },
    { key: 'reviewControl', value: report.kpis.reviewControl, icon: FiEye, color: '#8b5cf6' },
    { key: 'approvalActions', value: report.kpis.approvalActions, icon: FiCheckCircle, color: '#10b981' },
    { key: 'contractsCompleted', value: report.kpis.contractsCompleted, icon: FiFlag, color: '#14b8a6' },
  ] : [];

  // ── Detail grid columns ──────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      key: 'actionStart', label: t('activityReport.table.actionStart'), type: 'date',
      accessor: (r) => r.startedAt,
      render: (r) => (r.startedAt ? formatDateTimeFull(r.startedAt) : '—'),
    },
    {
      key: 'actionEnd', label: t('activityReport.table.actionEnd'), type: 'date',
      accessor: (r) => r.timestamp, render: (r) => formatDateTimeFull(r.timestamp),
    },
    {
      key: 'duration', label: t('activityReport.table.duration'), type: 'number',
      accessor: (r) => (r.durationMs == null ? null : r.durationMs),
      render: (r) => formatDuration(r.durationMs) || '—',
      rightAlign: true, noFilter: true,
    },
    { key: 'user', label: t('activityReport.table.user'), type: 'enum', accessor: (r) => r.userName || '—', groupable: true },
    { key: 'company', label: t('activityReport.table.company'), type: 'enum', accessor: (r) => r.companyName || '—', groupable: true },
    { key: 'department', label: t('activityReport.table.department'), type: 'enum', accessor: (r) => r.departmentName || '—', groupable: true },
    { key: 'contractNumber', label: t('activityReport.table.contractNumber'), type: 'text', accessor: (r) => r.contractNumber },
    { key: 'contractName', label: t('activityReport.table.contractName'), type: 'text', accessor: (r) => r.contractName },
    { key: 'contractType', label: t('activityReport.table.contractType'), type: 'enum', accessor: (r) => r.contractTypeName || '—', groupable: true },
    { key: 'step', label: t('activityReport.table.step'), type: 'enum', accessor: (r) => r.stepName || '—', groupable: true },
    {
      key: 'stepType', label: t('activityReport.table.stepType'), type: 'enum',
      accessor: (r) => r.stepType || '',
      optionLabel: (v) => (v ? t(`activityReport.stepTypes.${v}`) : '—'),
      render: (r) => (r.stepType ? t(`activityReport.stepTypes.${r.stepType}`) : '—'),
      groupable: true,
    },
    {
      key: 'action', label: t('activityReport.table.action'), type: 'enum',
      accessor: (r) => r.action,
      optionLabel: (v) => t(`activityReport.actions.${v}`, { defaultValue: v }),
      render: (r) => (
        <span className={`ar-action ar-action--${r.category}`}>
          {t(`activityReport.actions.${r.action}`, { defaultValue: r.action })}
        </span>
      ),
      groupable: true,
    },
    {
      key: 'description', label: t('activityReport.table.description'), type: 'text',
      accessor: (r) => describeRow(r),
      render: (r) => <span className="ar-desc">{describeRow(r) || '—'}</span>,
      noSort: true,
    },
  ], [t, describeRow, formatDuration]);

  return (
    <div className="activity-report">
      <PageHeader
        title={t('activityReport.title')}
        description={t('activityReport.subtitle', { count: report?.total || 0 })}
        actions={(
          <>
            <button className="btn btn-outline" onClick={handleExportExcel} disabled={!report || loading}>
              <FiFileText /> {t('activityReport.export.excel')}
            </button>
            <button className="btn btn-outline" onClick={handleExportPdf} disabled={!report || loading || exportingPdf}>
              {exportingPdf ? <FiDownload className="ar-spin" /> : <FiFile />} {t('activityReport.export.pdf')}
            </button>
          </>
        )}
      />

      {/* ── Filters ── */}
      <div className="ar-filters card">
        <div className="ar-filters__row">
          <div className="form-group">
            <label>{t('activityReport.filters.dateRange')}</label>
            <select value={preset} onChange={(e) => setPreset(e.target.value)}>
              <option value="week">{t('activityReport.presets.week')}</option>
              <option value="month">{t('activityReport.presets.month')}</option>
              <option value="year">{t('activityReport.presets.year')}</option>
              <option value="custom">{t('activityReport.presets.custom')}</option>
            </select>
          </div>
          {preset === 'custom' && (
            <>
              <div className="form-group">
                <label>{t('activityReport.filters.from')}</label>
                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              </div>
              <div className="form-group">
                <label>{t('activityReport.filters.to')}</label>
                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
              </div>
            </>
          )}
          <div className="form-group">
            <label>{t('activityReport.filters.company')}</label>
            <MultiSelect
              options={(meta?.companies || []).map((c) => ({ value: c.id, label: c.name }))}
              values={companyIds}
              onChange={setCompanyIds}
            />
          </div>
          <div className="form-group">
            <label>{t('activityReport.filters.user')}</label>
            <MultiSelect
              options={(meta?.users || []).map((u) => ({ value: u.id, label: u.name }))}
              values={userIds}
              onChange={setUserIds}
            />
          </div>
          <div className="form-group">
            <label>{t('activityReport.filters.department')}</label>
            <MultiSelect
              options={(meta?.departments || []).map((d) => ({ value: d.id, label: d.name }))}
              values={departmentIds}
              onChange={setDepartmentIds}
            />
          </div>
        </div>
        <div className="ar-filters__row">
          <div className="form-group">
            <label>{t('activityReport.filters.contractType')}</label>
            <MultiSelect
              options={(meta?.contractTypes || []).map((ct) => ({ value: ct.id, label: ct.name }))}
              values={contractTypeIds}
              onChange={setContractTypeIds}
            />
          </div>
          <div className="form-group">
            <label>{t('activityReport.filters.step')}</label>
            <MultiSelect options={stepOptions} values={stepNames} onChange={setStepNames} />
          </div>
          <div className="form-group">
            <label>{t('activityReport.filters.stepType')}</label>
            <MultiSelect options={stepTypeOptions} values={stepTypes} onChange={setStepTypes} />
          </div>
          <div className="form-group">
            <label>{t('activityReport.filters.action')}</label>
            <MultiSelect options={actionOptions} values={actions} onChange={setActions} />
          </div>
          <div className="form-group ar-filters__actions">
            <button className="btn btn-sm btn-outline" onClick={clearFilters}>
              {t('common.clearFilters')}
            </button>
          </div>
        </div>
      </div>

      {/* ── KPI cards ── */}
      <section className="ar-kpis">
        {kpis.map((k) => (
          <div key={k.key} className="ar-kpi">
            <span
              className="ar-kpi__tile"
              style={{ color: k.color, background: `color-mix(in srgb, ${k.color} 12%, transparent)` }}
            >
              <k.icon size={15} />
            </span>
            <div className="ar-kpi__body">
              <div className="ar-kpi__value">{loading ? '…' : k.value}</div>
              <div className="ar-kpi__label">{t(`activityReport.kpis.${k.key}`)}</div>
            </div>
          </div>
        ))}
      </section>

      {/* ── Per-user summary ── */}
      <div className="card ar-summary">
        <h3><FiBarChart2 /> {t('activityReport.summary.title')}</h3>
        <p className="ar-summary__hint">{t('activityReport.summary.hint')}</p>
        {loading ? (
          <div className="loading-spinner">{t('common.loading', { defaultValue: 'Loading…' })}</div>
        ) : !report?.userSummary?.length ? (
          <div className="empty-state"><p>{t('activityReport.noData')}</p></div>
        ) : (
          <div className="ar-summary__scroll">
            <table>
              <thead>
                <tr>
                  {['user', 'total', 'created', 'revision', 'review', 'control', 'approval', 'completed', 'other'].map((k) => (
                    <th key={k} className={k === 'user' ? '' : 'num'}>{t(`activityReport.summary.${k}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.userSummary.map((s) => (
                  <tr key={s.userId || 'none'}>
                    <td>{s.userName || '—'}</td>
                    <td className="num"><strong>{s.total}</strong></td>
                    <td className="num">{s.created}</td>
                    <td className="num">{s.revision}</td>
                    <td className="num">{s.review}</td>
                    <td className="num">{s.control}</td>
                    <td className="num">{s.approval}</td>
                    <td className="num">{s.completed}</td>
                    <td className="num">{s.other}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Detail list ── */}
      <h3 className="ar-details-title">{t('activityReport.detailsTitle')}</h3>
      {report?.truncated && (
        <p className="ar-truncated">{t('activityReport.truncatedNote', { shown: report.activities.length, total: report.total })}</p>
      )}
      <DataGrid
        columns={columns}
        rows={report?.activities || []}
        loading={loading}
        storageKey="activityReport:cols:v2"
        exportName={t('activityReport.export.fileName')}
        searchPlaceholder={t('activityReport.searchPlaceholder')}
        onRowClick={(r) => r.contractId && navigate(`/contracts/${r.contractId}`)}
        pageSizeOptions={[25, 50, 100]}
        emptyText={t('activityReport.noData')}
      />
    </div>
  );
}

export default ActivityReport;
